import WarningAmber from "@mui/icons-material/WarningAmber";
import {
	Box,
	SelectChangeEvent,
	Skeleton,
	Switch,
	Typography,
} from "@mui/material";
import React, { useMemo } from "react";

import { colors } from "../../../../../../config/colors";
import CutoffHoursInput from "../../../../../../design/cutoff-hours-input";
import { DEFAULT_CUTOFF_HOURS } from "../../../../../../utils/acceptance-cutoff";
import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import AllowedWorkcenters from "../flex-signup/allowed-workcenters";

interface WorkCenter {
	id: string | number;
	name: string;
	isShiftAcceptanceCutoffEnabled: boolean;
}

interface AcceptanceCutoffProps {
	newShiftSettings: {
		acceptanceCutoffEnabled: boolean;
		acceptanceCutoffMinHours: number;
	};
	setNewShiftSettings: React.Dispatch<React.SetStateAction<any>>;
	loading: boolean;
	acceptanceCutoffWorkcenters: any;
	newAcceptanceCutoffWorkcenters: any;
	setNewAcceptanceCutoffWorkcenters: React.Dispatch<React.SetStateAction<any>>;
}

const AcceptanceCutoff: React.FC<AcceptanceCutoffProps> = ({
	newShiftSettings,
	setNewShiftSettings,
	loading,
	acceptanceCutoffWorkcenters,
	newAcceptanceCutoffWorkcenters,
	setNewAcceptanceCutoffWorkcenters,
}) => {
	const {
		getLocationSettingsStatus,
		currentLocationData,
		updateAllowedWorkCentersForShiftsStatus,
	} = useGatAdminConfigData();

	const { wc, locationWorkcenters } = useMemo(() => {
		if (!currentLocationData?.workCenters) {
			return { wc: {}, locationWorkcenters: [] };
		}

		const wc = Object.fromEntries(
			currentLocationData.workCenters.map((wc: WorkCenter) => [wc.id, wc.name])
		);

		return {
			wc,
			locationWorkcenters: currentLocationData.workCenters,
		};
	}, [currentLocationData?.workCenters]);

	const selectedCutoffHours =
		newShiftSettings.acceptanceCutoffMinHours ?? DEFAULT_CUTOFF_HOURS;

	const handleWorkcenterChange = (
		event: SelectChangeEvent<typeof acceptanceCutoffWorkcenters>
	) => {
		const value = event.target.value;
		if (value.includes("all")) {
			if (newAcceptanceCutoffWorkcenters.length === locationWorkcenters.length) {
				setNewAcceptanceCutoffWorkcenters([]);
			} else {
				setNewAcceptanceCutoffWorkcenters(
					locationWorkcenters?.map((wc: { id: number }) => wc.id) || []
				);
			}
		} else {
			setNewAcceptanceCutoffWorkcenters(value as string[]);
		}
	};

	const handleDelete = (
		event: React.MouseEvent,
		workcenterToDelete: string
	) => {
		event.stopPropagation();
		if (!newShiftSettings.acceptanceCutoffEnabled) {
			return;
		}
		setNewAcceptanceCutoffWorkcenters(
			newAcceptanceCutoffWorkcenters.filter(
				(wc: string) => wc !== workcenterToDelete
			)
		);
	};

	return (
		<Box
			sx={{
				backgroundColor: "#F5F5F5",
				borderRadius: "8px",
				marginTop: "32px",
				padding: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "16px",
							color: "rgba(0, 0, 0, 0.87)",
						}}
					>
						Limit how late Workers can accept a Shift
					</Typography>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "12px",
							color: "rgba(0, 0, 0, 0.6)",
						}}
					>
						Stop Workers from accepting a Shift once it is close to starting.
						Supervisors can override this on any individual Shift.
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{loading || getLocationSettingsStatus === "pending" ? (
						<Skeleton width={50} height={40} />
					) : (
						<Switch
							checked={newShiftSettings.acceptanceCutoffEnabled ?? false}
							onChange={(e) => {
								setNewShiftSettings((prevSettings: any) => ({
									...prevSettings,
									acceptanceCutoffEnabled: e.target.checked,
									// Seed a default the first time the cutoff is enabled.
									acceptanceCutoffMinHours: e.target.checked
										? prevSettings.acceptanceCutoffMinHours ?? DEFAULT_CUTOFF_HOURS
										: prevSettings.acceptanceCutoffMinHours,
								}));
							}}
						/>
					)}
				</Box>
			</Box>

			{newShiftSettings.acceptanceCutoffEnabled ? (
				<Box
					sx={{
						marginTop: "32px",
					}}
				>
					<Box
						sx={{
							marginTop: "20px",
							marginBottom: "8px",
							borderRadius: "8px",
							border: 1,
							borderWidth: "1px",
							borderColor: "rgba(0,0,0,0.12)",
							background: "#FFF",
							padding: "16px",
						}}
					>
						<Box sx={{ display: "flex", flexDirection: "row" }}>
							{Array.isArray(locationWorkcenters) &&
							locationWorkcenters.length > 0 ? (
								<AllowedWorkcenters
									loading={
										loading ||
										updateAllowedWorkCentersForShiftsStatus === "pending" ||
										getLocationSettingsStatus === "pending"
									}
									selectedWorkcenters={newAcceptanceCutoffWorkcenters}
									handleWorkcenterChange={handleWorkcenterChange}
									handleDelete={handleDelete}
									locationWorkcenters={locationWorkcenters}
									wc={wc}
									updateStatus={updateAllowedWorkCentersForShiftsStatus}
								/>
							) : (
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontWeight: 400,
										fontSize: "12px",
										color: "rgba(0, 0, 0, 0.6)",
									}}
								>
									Please add a workcenter from the Workcenters section to be
									able to select it for acceptance cutoff.
								</Typography>
							)}
						</Box>
						{Array.isArray(locationWorkcenters) &&
						locationWorkcenters.length > 0 &&
						newAcceptanceCutoffWorkcenters.length === 0 ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: "4px",
									marginLeft: "8px",
									marginTop: "4px",
								}}
							>
								<WarningAmber sx={{ color: colors.error, fontSize: "14px" }} />
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontWeight: 400,
										fontSize: "12px",
										color: colors.error,
									}}
								>
									Select at least 1 workcenter to save.
								</Typography>
							</Box>
						) : null}
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontWeight: 400,
										fontSize: "16px",
										color: "rgba(0, 0, 0, 0.87)",
										marginLeft: "8px",
									}}
								>
									Close acceptance this long before the Shift starts
								</Typography>
							</Box>

							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									alignItems: "center",
									marginLeft: "8px",
								}}
							>
								{getLocationSettingsStatus === "pending" ? (
									<Skeleton width={160} height={50} />
								) : (
									<CutoffHoursInput
										value={selectedCutoffHours}
										onChange={(hours) =>
											setNewShiftSettings((prevSettings: any) => ({
												...prevSettings,
												acceptanceCutoffMinHours: hours,
											}))
										}
									/>
								)}
							</Box>
						</Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 400,
								fontSize: "12px",
								color: "rgba(0, 0, 0, 0.6)",
								marginLeft: "8px",
								marginTop: "4px",
							}}
						>
							Enter up to 168 hours. Minutes are 00 or 30, minimum 00:30.
						</Typography>
					</Box>
				</Box>
			) : null}
		</Box>
	);
};

export default AcceptanceCutoff;
